import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";

interface RPEInputProps {
  value?: number | null;
  targetRPE?: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
  "data-testid"?: string;
}

const RPE_COLORS: Record<number, string> = {
  1: "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400",
  2: "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400",
  3: "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400",
  4: "bg-lime-100 text-lime-800 hover:bg-lime-200 dark:bg-lime-900/30 dark:text-lime-400",
  5: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400",
  6: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400",
  6.5: "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400",
  7: "bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
  7.5: "bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
  8: "bg-orange-200 text-orange-900 hover:bg-orange-300 dark:bg-orange-900/50 dark:text-orange-300",
  8.5: "bg-orange-200 text-orange-900 hover:bg-orange-300 dark:bg-orange-900/50 dark:text-orange-300",
  9: "bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400",
  9.5: "bg-red-200 text-red-900 hover:bg-red-300 dark:bg-red-900/50 dark:text-red-300",
  10: "bg-red-200 text-red-900 hover:bg-red-300 dark:bg-red-900/50 dark:text-red-300",
};

const RPE_LABELS: Record<number, string> = {
  1: "Çok kolay",
  2: "Kolay",
  3: "Kolay",
  4: "Hafif",
  5: "Orta",
  6: "Orta-zor",
  6.5: "Orta-zor",
  7: "Zor",
  7.5: "Zor",
  8: "Çok zor",
  8.5: "Çok zor",
  9: "Limit",
  9.5: "Limit",
  10: "Max",
};

export function RPEInput({
  value,
  targetRPE,
  onChange,
  disabled = false,
  className,
  "data-testid": dataTestId,
}: RPEInputProps) {
  const rpeValues = [1, 2, 3, 4, 5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];

  return (
    <div className={cn("w-20", className)}>
      <Select
        value={value ? value.toString() : "none"}
        onValueChange={(val) => {
          if (val === "none") {
            onChange(null);
          } else {
            onChange(Number(val));
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger
          data-testid={dataTestId}
          className={cn(
            "h-9 w-full justify-center px-1 text-center font-medium text-xs transition-all",
            value
              ? RPE_COLORS[value]
              : "border-input bg-muted/30 text-muted-foreground hover:bg-muted/50",
            targetRPE &&
              value !== targetRPE &&
              "border-primary/40 border-dashed",
          )}
        >
          <span className="truncate">{value ? `RPE ${value}` : "RPE"}</span>
        </SelectTrigger>
        <SelectContent className="min-w-[120px] border border-border bg-background text-foreground text-xs">
          <SelectItem value="none" className="text-muted-foreground">
            RPE Yok
          </SelectItem>
          {rpeValues.map((rpe) => (
            <SelectItem
              key={rpe}
              value={rpe.toString()}
              className="text-foreground"
            >
              RPE {rpe} {RPE_LABELS[rpe] ? `(${RPE_LABELS[rpe]})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface RPEBadgeProps {
  value?: number | null;
  className?: string;
}

export function RPEBadge({ value, className }: RPEBadgeProps) {
  if (value === null || value === undefined) {
    return (
      <span className={cn("text-muted-foreground text-xs", className)}>-</span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-md px-2 py-0.5 font-medium text-xs",
        RPE_COLORS[value],
        className,
      )}
    >
      {value}
    </span>
  );
}
